<template>
  <div v-if="isLISA3" ref="lisa3RowRef" class="input-row" v-bind="rowAttrs">
    <LISAInput v-if="!('default' in $slots) && type == null" v-bind="field"></LISAInput>
    <LISAInput v-else-if="type === 'text'" v-bind="field">
      <template v-if="'prepend' in $slots" #prepend>
        <slot name="prepend" />
      </template>
      <template v-if="'append' in $slots" #append>
        <slot name="append" />
      </template>
    </LISAInput>
    <LISAInput v-else-if="type === 'textarea'" v-bind="field" type="textarea" :style="[inputStyle]">
      <template v-if="'prepend' in $slots" #prepend>
        <slot name="prepend"></slot>
      </template>
      <template v-if="'append' in $slots" #append>
        <slot name="append" />
      </template>
      <template v-if="'label' in $slots" #label>
        <slot name="label" :display-name="field.label" />
      </template>
    </LISAInput>
    <LISACheckbox v-else-if="isLISA3 && type === 'checkbox'" v-bind="field"> </LISACheckbox>
    <LISASelect
      v-else-if="type === 'select'"
      v-bind="{
        ...field,
        options: field.selectOptions,
        groups: field.selectGroups,
        selectionDisplay: field.selectionDisplay,
      }"
    >
      <template v-if="'prepend' in $slots" #prepend>
        <slot name="prepend" />
      </template>
      <template v-if="'append' in $slots" #append>
        <slot name="append" />
      </template>
    </LISASelect>
    <LISANumber v-else-if="type === 'number'" type="number" v-bind="field">
      <template v-if="'prepend' in $slots" #prepend>
        <slot name="prepend" />
      </template>
      <template v-if="'append' in $slots" #append>
        <slot name="append" />
      </template>
    </LISANumber>
    <LISA3PasswordInput v-else-if="type === 'password'" v-bind="field">
      <template v-if="'prepend' in $slots" #prepend>
        <slot name="prepend" />
      </template>
      <template v-if="'append' in $slots" #append>
        <slot name="append" />
      </template>
    </LISA3PasswordInput>
    <template v-else>
      <slot :field="field" :validation="validation"></slot>
    </template>
  </div>
  <div
    v-else
    class="field input-row"
    :class="[
      inputClass,
      {
        'mb-0': small || type === 'hidden',
        'h-100': expandHeight,
        'input-row--transparent': transparentLayout,
      },
    ]"
    :data-validation-status="validation.status"
    v-bind="rowAttrs"
  >
    <div class="input-row__content">
      <label
        v-if="
          !['checkbox'].includes(type ?? '') &&
          displayName != null &&
          !hideLabel &&
          !floatingLabel &&
          type !== 'hidden'
        "
        :for="controlId"
        :class="labelClass"
        class="pb-1 inline-block"
      >
        {{ displayName }}
        <InfoTooltipVue
          v-if="ENV.VITE_APP_VALIDATION_DEBUG"
          class="ml-1"
          title="Validation Debug"
          icon="fas fa-bug fa-xs"
          :variant="!validation.invalid ? 'success' : 'error'"
          :aria-label="`Validation debug for ${displayName}`"
          experimental-design
        >
          <p v-for="text in validationDebugText" :key="`a[${text}]`">
            {{ text }}
          </p>
        </InfoTooltipVue>
        <InfoTooltipVue v-if="tooltipOptions" class="ml-1" v-bind="tooltipOptions" />
        <slot name="displayNameAppend" />
      </label>

      <component
        :is="$slots.prepend != null || $slots.append != null ? InputGroup : 'div'"
        ref="controlContainerRef"
        :class="{
          'mb-0': small || type === 'hidden',
          'flex align-items-center': ['checkbox'].includes(type ?? ''),
        }"
        class="h-100 input-row__control-container"
      >
        <FloatLabel v-if="usesFloatingLabel && isLISA3 === false" variant="on" class="h-100">
          <InputGroupAddon v-if="$slots.prepend != null" class="p-0">
            <slot name="prepend" :field="field" :validation="validation" />
          </InputGroupAddon>

          <slot :field="field" :validation="validation">
            <component
              :is="inputTypeComputed"
              ref="inputRef"
              :class="{ 'p-filled': $slots.prepend }"
              v-bind="{ ...$attrs, ...field }"
              :auto-resize="autoResize"
              :style="[inputStyle, { resize: resolveResizeStyle(resize) }]"
              @keydown.meta.enter="submitArea"
              @keydown.ctrl.enter="submitArea"
            />
          </slot>

          <div v-if="['checkbox'].includes(type ?? '')" class="pl-2" style="display: inline-block">
            <label
              v-if="displayName != null && !hideLabel"
              :for="controlId"
              :class="[labelClass, { 'cursor-pointer': !isDisabled }]"
              @click="changeCheckboxValue"
            >
              {{ displayName }}
              <InfoTooltipVue
                v-if="ENV.VITE_APP_VALIDATION_DEBUG"
                class="ml-1"
                title="Validation Debug"
                icon="fas fa-bug fa-xs"
                :variant="!validation.invalid ? 'success' : 'error'"
                :aria-label="`Validation debug for ${displayName}`"
                experimental-design
              >
                <p v-for="text in validationDebugText" :key="`a[${text}]`">
                  {{ text }}
                </p>
              </InfoTooltipVue>
              <InfoTooltipVue v-if="tooltipOptions" class="ml-1" v-bind="tooltipOptions" />
              <slot name="displayNameAppend" />
            </label>
          </div>

          <label v-if="floatingLabel && !hideLabel" :for="controlId" class="z-2">
            <span>{{ displayName }}</span>
            <InfoTooltipVue
              v-if="ENV.VITE_APP_VALIDATION_DEBUG"
              class="ml-1"
              title="Validation Debug"
              icon="fas fa-bug fa-xs"
              :variant="!validation.invalid ? 'success' : 'error'"
              :aria-label="`Validation debug for ${displayName}`"
              experimental-design
            >
              <p v-for="text in validationDebugText" :key="`a[${text}]`">
                {{ text }}
              </p>
            </InfoTooltipVue>
            <InfoTooltipVue
              v-if="tooltipOptions"
              class="ml-1"
              v-bind="tooltipOptions"
              size="fa-xs"
              :title="displayName"
              experimental-design
            />
            <slot name="displayNameAppend" />
          </label>

          <InputGroupAddon v-if="$slots.append != null" class="p-0" :class="appendClass">
            <slot name="append" />
          </InputGroupAddon>
        </FloatLabel>

        <template v-else>
          <InputGroupAddon v-if="$slots.prepend != null" class="p-0">
            <slot name="prepend" :field="field" :validation="validation" />
          </InputGroupAddon>

          <slot :field="field" :validation="validation">
            <component
              :is="inputTypeComputed"
              ref="inputRef"
              v-bind="{ ...$attrs, ...field }"
              :multiple="!!multiple"
              :auto-resize="autoResize"
              :style="[inputStyle, { display: type === 'hidden' ? 'none' : '' }]"
              @keydown.meta.enter="submitArea"
              @keydown.ctrl.enter="submitArea"
            />
          </slot>

          <div v-if="['checkbox'].includes(type ?? '')" class="pl-2" style="display: inline-block">
            <label
              v-if="displayName != null && !hideLabel"
              :for="controlId"
              :class="[labelClass, { 'cursor-pointer': !isDisabled }]"
              @click="changeCheckboxValue"
            >
              {{ displayName }}
              <InfoTooltipVue
                v-if="ENV.VITE_APP_VALIDATION_DEBUG"
                class="ml-1"
                title="Validation Debug"
                icon="fas fa-bug fa-xs"
                variant="secondary"
                :aria-label="`Validation debug for ${displayName}`"
                experimental-design
              >
                <p v-for="text in validationDebugText" :key="`a[${text}]`">
                  {{ text }}
                </p>
              </InfoTooltipVue>
              <InfoTooltipVue v-if="tooltipOptions" class="ml-1" v-bind="tooltipOptions" />
              <slot name="displayNameAppend" />
            </label>
          </div>

          <InputGroupAddon v-if="$slots.append != null" class="p-0" :class="appendClass">
            <slot name="append" />
          </InputGroupAddon>
        </template>
      </component>

      <Message
        v-if="type === 'hidden' && validation.invalid && !hideErrorMessage"
        severity="error"
        icon="fas fa-exclamation-triangle"
      >
        {{ displayName }}
      </Message>
    </div>

    <small
      v-if="type !== 'hidden' && invalidFeedback != null && !hideErrorMessage"
      :id="errorId"
      style="color: var(--p-lisa-invalid)"
    >
      <slot name="errorMessage" :invalid-feedback="invalidFeedback">
        {{ invalidFeedback }}
      </slot>
    </small>
    <small v-else-if="description">{{ description }}</small>
  </div>
</template>

<script setup lang="ts" generic="TValue = unknown">
import {
  type Component,
  type ComponentPublicInstance,
  type StyleValue,
  computed,
  getCurrentInstance,
  inject,
  nextTick,
  onMounted,
  useId,
  useAttrs,
  useTemplateRef,
} from "vue";
import { useTranslation } from "@/language/translation";
import Checkbox from "primevue/checkbox";
import FloatLabel from "primevue/floatlabel";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputText from "primevue/inputtext";
import Message from "primevue/message";
import Textarea from "primevue/textarea";
import InfoTooltipVue from "../InfoTooltip.vue";
import LISAInputNumber from "@/components/primevueWrapper/LISAInputNumber.vue";
import {
  FormValidationContextKey,
  type EqualityComparator,
  type ValidationRule,
} from "@/modules/formValidation/types/validation";
import { useFieldValidation } from "@/modules/formValidation/composables/useFieldValidation";
import { ENV } from "@/ENV.ts";
import {
  LISACheckbox,
  LISAInput,
  LISANumber,
  LISASelect,
  type LISAEmptyAction,
  type LISASelectGroup,
  type LISASelectOption,
} from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3.ts";
import LISA3PasswordInput from "../LISA3PasswordInput.vue";

type InputRowClass = string | Record<string, boolean> | readonly InputRowClass[];
type InputRowType = "number" | "text" | "textarea" | "checkbox" | "hidden" | "password" | "select";

interface InputRowProps {
  modelValue?: TValue;
  name: string;
  displayName?: string;
  inputId?: string;
  placeholder?: string;
  rules?: readonly ValidationRule<TValue>[];
  required?: boolean;
  comparator?: EqualityComparator<TValue>;
  trackDirty?: boolean;
  description?: string;
  inputType?: Component;
  tooltipOptions?: InstanceType<typeof InfoTooltipVue>["$props"] | null;
  labelClass?: InputRowClass;
  inputClass?: InputRowClass;
  inputStyle?: StyleValue;
  type?: InputRowType | null;
  disabled?: boolean;
  small?: boolean;
  readonly?: boolean;
  hideLabel?: boolean;
  appendClass?: InputRowClass;
  hideErrorMessage?: boolean;
  testIdOverride?: string;
  autoFocus?: boolean;
  autoResize?: boolean;
  resize?: boolean | "vertical" | "horizontal" | "both";
  expandHeight?: boolean;

  // lisa3
  loading?: boolean;
  tooltip?: string;
  label?: string;
  fullHeight?: boolean;
  fullWidth?: boolean;
  // type: select
  selectOptions?: LISASelectOption[];
  selectGroups?: LISASelectGroup[];
  multiple?: boolean;
  emptyActions?: readonly LISAEmptyAction[];
  selectionDisplay?: "chips" | "text";
  // type: textarea
  rows?: number | string;
  autosize?: boolean;
  // type: number
  min?: number;
  max?: number;
  maxLength?: number;
}

const props = withDefaults(defineProps<InputRowProps>(), {
  modelValue: undefined,
  displayName: undefined,
  inputId: undefined,
  placeholder: undefined,
  rules: () => [],
  required: false,
  comparator: undefined,
  trackDirty: true,
  description: undefined,
  inputType: undefined,
  tooltipOptions: null,
  labelClass: "",
  inputClass: undefined,
  inputStyle: undefined,
  type: null,
  disabled: false,
  small: false,
  readonly: false,
  hideLabel: false,
  appendClass: "",
  hideErrorMessage: false,
  testIdOverride: undefined,
  autoFocus: false,
  autoResize: true,
  resize: true,
  expandHeight: false,
  // lisa3
  autosize: false,
  loading: false,
  fullHeight: false,
  fullWidth: false,
  // type select
  selectGroups: undefined,
  selectOptions: undefined,
  multiple: false,
  emptyActions: undefined,
  rows: 2,
  min: undefined,
  max: undefined,
  maxLength: undefined,
  tooltip: undefined,
  label: undefined,
  selectionDisplay: undefined,
});

const floatingLabel = isLISA3;

const emit = defineEmits<{
  "update:modelValue": [value: TValue];
}>();

const { t } = useTranslation();
const instance = getCurrentInstance();
const attrs = useAttrs();
const rowAttrs = computed(() => ({ class: attrs.class, style: attrs.style }));
const controlAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== "class" && key !== "style")),
);
const formContext = inject(FormValidationContextKey, undefined);
const generatedId = useId();
const controlId = computed(() => props.inputId ?? `input-row-${generatedId}`);
const errorId = computed(() => `${controlId.value}-error`);
const model = computed<TValue>(() => props.modelValue as TValue);
const isDisabled = computed(() => props.disabled || formContext?.disabled === true);
const usesFloatingLabel = computed(
  () =>
    floatingLabel.value &&
    props.displayName != null &&
    !props.hideLabel &&
    props.type !== "checkbox" &&
    props.type !== "hidden",
);

function resolveResizeStyle(resize: InputRowProps["resize"]) {
  if (resize === false) return "none";
  if (typeof resize === "string") return resize;
  return "";
}
const transparentLayout = computed(
  () =>
    props.displayName == null &&
    instance?.slots.default != null &&
    instance.slots.prepend == null &&
    instance.slots.append == null &&
    instance.attrs.class == null &&
    instance.attrs.style == null &&
    !usesFloatingLabel.value,
);

const validation = useFieldValidation({
  value: model,
  name: () => props.name,
  displayName: () => props.displayName ?? props.name,
  rules: () => props.rules,
  required: () => props.required,
  disabled: isDisabled,
  trackDirty: () => props.trackDirty,
  comparator: () => props.comparator,
  updateValue: (value) => emit("update:modelValue", value),
});

const hasValidation = computed(() => props.required || props.rules.length > 0);
const validationClass = computed(() => ({
  "p-valid": hasValidation.value && validation.status === "valid",
  "p-invalid": hasValidation.value && validation.status === "invalid",
}));
const validationDebugText = computed(() => {
  const configuredRules = [
    `required=${props.required}`,
    ...props.rules.map((rule) => {
      const dependencies =
        rule.dependencies.length > 0
          ? ` dependencies=[${rule.dependencies.map(formatDebugValue).join(", ")}]`
          : "";
      const debounce = rule.kind === "async" ? ` debounce=${rule.debounceMs ?? 300}ms` : "";
      return `${rule.name} (${rule.kind})${dependencies}${debounce}`;
    }),
  ];

  return [
    `status=${validation.status}`,
    `valid=${String(validation.valid)}`,
    `invalid=${validation.invalid}`,
    `pending=${validation.pending}`,
    `baselinePending=${validation.baselinePending}`,
    `dirty=${validation.dirty}`,
    `touched=${validation.touched}`,
    `current=${formatDebugValue(validation.value)}`,
    `baseline=${formatDebugValue(validation.baseline)}`,
    `issue=${validation.issue?.messageKey ?? "none"}`,
    `rules: ${configuredRules.join("; ")}`,
  ];
});
const invalidFeedback = computed(() => {
  if (!validation.touched || !validation.invalid || validation.issue == null) {
    return undefined;
  }
  return t(validation.issue.messageKey, {
    field: props.displayName ?? props.name,
    ...validation.issue.params,
  });
});

function formatDebugValue(value: unknown): string {
  if (typeof value === "string") {
    return JSON.stringify(value);
  }
  if (
    value == null ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }

  try {
    const serialized = JSON.stringify(value);
    return serialized == null ? String(value) : serialized;
  } catch {
    return Object.prototype.toString.call(value);
  }
}

const field = computed(() => ({
  ...controlAttrs.value,
  id: controlId.value,
  name: props.name,
  loading: props.loading,
  autofocus: props.autoFocus,
  modelValue: props.modelValue as any,
  "onUpdate:modelValue": (value: unknown) => emit("update:modelValue", value as TValue),
  required: props.required,
  state: validation.valid,
  stateAsString: validation.valid?.toString(),
  errorMessage:
    validation.valid === false ? (invalidFeedback.value ?? "Bitte ausfüllen") : undefined,
  disabled: isDisabled.value,
  readonly: props.readonly,
  placeholder: props.placeholder,
  multiple: props.multiple,
  min: props.min,
  max: props.max,
  maxlength: props.maxLength,
  autosize: props.autosize,
  rows: props.rows,
  class: [{ "w-100": props.type !== "checkbox" }, validationClass.value],
  style: props.inputStyle,
  valid: validation.valid,
  binary: props.type === "checkbox" ? true : undefined,
  "data-testid":
    props.testIdOverride ??
    controlAttrs.value["data-testid"] ??
    controlAttrs.value["data-testId"] ??
    props.name,
  "data-testvalue": props.modelValue,
  "aria-invalid": validation.invalid || undefined,
  "aria-busy": validation.pending || undefined,
  "aria-describedby": invalidFeedback.value != null ? errorId.value : undefined,
  pt: {
    root: {
      class: props.inputClass,
      name: props.name,
    },
    pcInputText: {
      root: {
        class: props.inputClass,
        name: props.name,
      },
    },
  },
  // lisa3
  selectOptions: props.selectOptions,
  selectGroups: props.selectGroups,
  selectionDisplay: props.selectionDisplay,
  tooltip: props.tooltip ?? props.tooltipOptions?.text,
  label: props.label ?? props.displayName,
  emptyActions: props.emptyActions,
  fullWidth: props.fullWidth,
  fullHeight: props.fullHeight,
  invalid: validation.valid === false,
  hideLabel: props.hideLabel,
}));

const inputTypeComputed = computed(() => {
  if (props.inputType != null) {
    return props.inputType;
  }
  if (props.type === "number") {
    return LISAInputNumber;
  }
  if (props.type === "checkbox") {
    return Checkbox;
  }
  if (props.type === "textarea") {
    return Textarea;
  }
  return InputText;
});

function changeCheckboxValue() {
  if (!isDisabled.value) {
    emit("update:modelValue", !props.modelValue as TValue);
  }
}

function submitArea() {
  if (props.type === "textarea" && formContext?.controller.canSubmit) {
    resolveInputElement()?.closest("form")?.requestSubmit();
  }
}

const inputRef = useTemplateRef<ComponentPublicInstance | HTMLElement>("inputRef");
const lisa3RowRef = useTemplateRef<HTMLElement>("lisa3RowRef");
const controlContainerRef = useTemplateRef<ComponentPublicInstance | HTMLElement>(
  "controlContainerRef",
);

function resolveElement(
  target: ComponentPublicInstance | HTMLElement | null,
): HTMLElement | undefined {
  if (target instanceof HTMLElement) {
    return target;
  }
  return target?.$el instanceof HTMLElement ? target.$el : undefined;
}

function resolveInputElement(): HTMLElement | undefined {
  return resolveElement(inputRef.value) ?? resolveElement(controlContainerRef.value);
}

async function focusInput() {
  if (!props.autoFocus) {
    return;
  }
  await nextTick();
  const element = resolveInputElement() ?? lisa3RowRef.value ?? undefined;
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    element.focus();
    return;
  }
  const nestedInput = element?.querySelector("input, textarea");
  if (nestedInput instanceof HTMLElement) {
    nestedInput.focus();
  }
}

onMounted(() => {
  if (
    import.meta.env.DEV &&
    props.trackDirty &&
    props.type !== "hidden" &&
    instance?.slots.default == null &&
    instance?.vnode.props?.["onUpdate:modelValue"] == null
  ) {
    // eslint-disable-next-line no-console
    console.warn(`InputRow "${props.name}" tracks dirty state without a writable v-model`);
  }
  void focusInput();
});

defineExpose(validation);
</script>

<style lang="scss" scoped>
.input-row,
.input-row__content,
.input-row__control-container,
.p-floatlabel {
  min-width: 0;
}

.input-row__content,
.input-row__control-container,
.p-floatlabel {
  width: 100%;
  max-width: 100%;
}

.input-row--transparent,
.input-row--transparent > .input-row__content,
.input-row--transparent > .input-row__content > .input-row__control-container {
  display: contents;
}

:deep(.float-label-small:not(:has(input:focus)) label) {
  transform: translateY(-100%);
}

.p-floatlabel:has(.p-disabled) label {
  color: var(--p-secondary-color) !important;
}

.input-row__compact-checkbox-addon {
  flex: 0 0 2.25rem;
  min-width: 2.25rem;
}
</style>
